import dayjs from "dayjs"
import { Box, Button, Dialog, DialogContent, Typography } from "@mui/material"
import { grey } from "@mui/material/colors"
import { BoxFC } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { SystemUpdateAltRounded } from "resource/muiImport"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { uploadReceiptColumns, getDupDocNoSet, dupDocNoSx } from "./columns"
import FullScreenImageDialog from "components/FullScreenImageDialog/FullScreenImageDialog"
import { alertConfirm, alertError } from "components/Alert"
import SelectJobOrderDialog from "components/SelectJobOrderDialog/SelectJobOrderDialog"

// Gemini reads files one-by-one on the server; keep each request small so it stays
// under multer's 20-file cap and the 60s socket timeout, and so rows stream in progressively.
const CHUNK_SIZE = 4

const UploadReceiptDialog = ({ dialogOpen, setDialogOpen, onFinish, pcTrnsId = null, fallbackJobOrdId = null }) => {
  const { ax, msData } = useContext(GlobalStateContext)
  const [totalCount, setTotalCount] = useState(0)
  const [processedCount, setProcessedCount] = useState(0)
  const [results, setResults] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [selectionModel, setSelectionModel] = useState([])
  // job orders matching a typed container no, waiting for the user to pick one
  const [jobOrderChoices, setJobOrderChoices] = useState([])
  const [jobOrderTargetIds, setJobOrderTargetIds] = useState([])
  const [selectJobOrderOpen, setSelectJobOrderOpen] = useState(false)
  const [imgDialogOpen, setImgDialogOpen] = useState(false)
  const [imgUrl, setImgUrl] = useState("")

  const handleImageClick = useCallback((url) => {
    setImgUrl(url)
    setImgDialogOpen(true)
  }, [])

  const dupDocNos = useMemo(() => getDupDocNoSet(results), [results])

  const columns = useMemo(() => uploadReceiptColumns({
    expCombo: msData.expOprCombo, supplierCombo: msData.supplierCombo, onImageClick: handleImageClick, dupDocNos
  }), [msData.expOprCombo, msData.supplierCombo, handleImageClick, dupDocNos])

  const uploadFiles = useCallback(async (fileList) => {
    if (fileList.length === 0) return
    setIsUploading(true)
    setTotalCount(t => t + fileList.length)
    for (let i = 0; i < fileList.length; i += CHUNK_SIZE) {
      const chunk = fileList.slice(i, i + CHUNK_SIZE)
      const formData = new FormData()
      chunk.forEach(f => formData.append("files", f))
      if (pcTrnsId) formData.append("PCTrnsId", pcTrnsId)
      if (fallbackJobOrdId) formData.append("JobOrdId", fallbackJobOrdId)
      try {
        const value = await ax.post("/petty-cash/uploadReceiptFromUser", formData)
        setResults(prev => {
          const startNo = prev.length
          const rows = value?.data
            ? value.data.map((item, idx) => ({ ...item, id: item.PCItmId ?? -(startNo + idx + 1), No: startNo + idx + 1 }))
            : chunk.map((f, idx) => ({ id: -(startNo + idx + 1), No: startNo + idx + 1, OrigFileNm: f.name, error: "อัปโหลดไม่สำเร็จ" }))
          return [...prev, ...rows]
        })
      } catch (err) {
        // network/timeout on this batch — mark its files as failed and keep going
        setResults(prev => {
          const startNo = prev.length
          const rows = chunk.map((f, idx) => ({ id: -(startNo + idx + 1), No: startNo + idx + 1, OrigFileNm: f.name, error: "อัปโหลดไม่สำเร็จ" }))
          return [...prev, ...rows]
        })
      }
      setProcessedCount(c => c + chunk.length)
    }
    setIsUploading(false)
  }, [ax, pcTrnsId, fallbackJobOrdId])

  const handleSelectFiles = useCallback((e) => {
    uploadFiles(Array.from(e.target.files))
  }, [uploadFiles])

  const handleDragOver = useCallback((e) => { e.preventDefault() }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const dropped = e.dataTransfer.items
      ? Array.from(e.dataTransfer.items).filter(i => i.kind === 'file').map(i => i.getAsFile())
      : Array.from(e.dataTransfer.files)
    uploadFiles(dropped)
  }, [uploadFiles])

  const handlePaste = useCallback((e) => {
    const images = Array.from(e.clipboardData.items)
      .filter(item => item.type.startsWith('image/'))
      .map(item => item.getAsFile())
    if (images.length > 0) uploadFiles(images)
  }, [uploadFiles])

  // Write the picked job order onto the given rows (grid columns follow the picked job order)
  const applyJobOrder = useCallback((jobOrder, targetIds) => {
    const idArgs = targetIds.length === 1
      ? { PCItmId: Number(targetIds[0]) }
      : { PCItmIds: targetIds.map(Number) }
    ax.post("/petty-cash/updatePCItemCols", { ...idArgs, ColValues: { JobOrdId: jobOrder.JobOrdId } }, false)
    setResults(o => o.map(row => targetIds.includes(row.id) ? {
      ...row,
      JobOrdId: jobOrder.JobOrdId, ContNo: jobOrder.ContNo, JobNo: jobOrder.JobNo, Bkg: jobOrder.Bkg,
      JobSNm: jobOrder.JobSNm, JobRcptBrchNo: jobOrder.JobRcptBrchNo, JobRcptTaxId: jobOrder.JobRcptTaxId,
    } : row))
  }, [ax])

  // Apply one committed cell edit to every target row (the edited row, or the whole selection)
  const applyEdit = useCallback((params, targetIds) => {
    // single row keeps sending PCItmId so the log keeps its RecId; many rows go in one call
    const idArgs = targetIds.length === 1
      ? { PCItmId: Number(targetIds[0]) }
      : { PCItmIds: targetIds.map(Number) }
    let value = params.value
    if (value === "" || value === undefined || (typeof value === "number" && isNaN(value))) value = null
    if (params.field === "RcptDte" && value) value = dayjs(value).format("YYYY-MM-DD")

    // ContNo is not a pc_item column: look up the job orders of that container instead.
    // One hit is applied straight away, several hits let the user pick one.
    if (params.field === "ContNo") {
      const contNo = (value || "").toString().trim()
      if (contNo.length < 4) {
        alertError("กรุณาระบุหมายเลขตู้อย่างน้อย 4 ตัวอักษร")
        return
      }
      ax.post("/petty-cash/getJobOrderByContNo", { ContNo: contNo }).then(value => {
        if (!value.data || value.data.length === 0) return
        if (value.data.length === 1) {
          applyJobOrder(value.data[0], targetIds)
          return
        }
        setJobOrderChoices(value.data)
        setJobOrderTargetIds(targetIds)
        setSelectJobOrderOpen(true)
      })
      return
    }

    // grid fields RcptNo/RcptDte are aliases; physical pc_item columns are DocNo/DocDte
    const colMap = { RcptNo: "DocNo", RcptDte: "DocDte" }
    const dbCol = colMap[params.field] || params.field

    // editing the receipt no. invalidates the server-side duplicate flag; in-grid duplicates
    // are still detected from the rows themselves
    setResults(o => o.map(row => targetIds.includes(row.id)
      ? { ...row, [params.field]: value, ...(params.field === "RcptNo" ? { DupDocNoCnt: 0 } : {}) }
      : row))
    ax.post("/petty-cash/updatePCItemCols", {
      ...idArgs,
      ColValues: { [dbCol]: value },
    }, false)
  }, [ax, applyJobOrder])

  const handleEditCommit = useCallback((params) => {
    const editableFields = ["ExpId", "Amnt", "RcptNo", "RcptDte", "JobOrdId", "SupId", "ContNo"]
    if (!editableFields.includes(params.field)) return

    // rows whose upload failed have no PCItmId — there is nothing to save for them
    const isSaved = (id) => results.some(row => row.id === id && row.PCItmId)
    if (!isSaved(params.id)) return

    const isMulti = selectionModel.length > 1 && selectionModel.includes(params.id)
    const targetIds = (isMulti ? selectionModel : [params.id]).filter(isSaved)
    if (targetIds.length === 0) return

    if (isMulti) {
      alertConfirm(`ต้องการแก้ไข "${params.colDef.headerName}" ของ ${targetIds.length} รายการที่เลือก พร้อมกันหรือไม่`
        , () => applyEdit(params, targetIds))
    } else {
      applyEdit(params, targetIds)
    }
  }, [applyEdit, results, selectionModel])

  const handleClose = useCallback(() => {
    setDialogOpen(false)
    if (results.some(r => r.PCItmId)) onFinish?.()
  }, [setDialogOpen, onFinish, results])

  useEffect(() => {
    if (dialogOpen) {
      document.addEventListener('paste', handlePaste)
    } else {
      setTotalCount(0)
      setProcessedCount(0)
      setResults([])
      setSelectionModel([])
    }
    return () => document.removeEventListener('paste', handlePaste)
  }, [dialogOpen, handlePaste])

  return (
    <Dialog
      open={dialogOpen}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='lg'
    >
      <DialogHeader handleDialogClose={handleClose} title="อัปโหลดใบเสร็จ" onDeleteClick={null} />
      <DialogContent>
        <BoxFC pt={2} gap={2}>
          <Box
            minHeight={130}
            border={`2px dashed ${grey[400]}`}
            borderRadius={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{ bgcolor: grey[50] }}
          >
            <SystemUpdateAltRounded sx={{ fontSize: 40, color: grey[500] }} />
            <Typography color="text.secondary">
              {isUploading
                ? `กำลังประมวลผล... ${processedCount}/${totalCount} ไฟล์`
                : totalCount > 0 ? `อ่านแล้ว ${totalCount} ไฟล์` : "ลากไฟล์มาวางที่นี่ หรือ Ctrl+V วางรูปภาพ"}
            </Typography>
            <Button variant="outlined" component="label" size="small" disabled={isUploading}>
              เลือกไฟล์
              <input type="file" hidden multiple accept="image/*,application/pdf" onChange={handleSelectFiles} />
            </Button>
          </Box>
          <Box height={500}>
            <DataGridCellExpand
              hideFooter
              checkboxSelection
              disableSelectionOnClick
              selectionModel={selectionModel}
              onSelectionModelChange={setSelectionModel}
              rows={results}
              columns={columns}
              onCellEditCommit={handleEditCommit}
              sx={dupDocNoSx}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <DialogFooter cancelText="ปิด" handleDialogClose={handleClose} />
      <FullScreenImageDialog
        linkUrl={imgUrl}
        dialogOpen={imgDialogOpen}
        setDialogOpen={setImgDialogOpen}
      />
      <SelectJobOrderDialog
        dialogOpen={selectJobOrderOpen}
        setDialogOpen={setSelectJobOrderOpen}
        jobOrders={jobOrderChoices}
        onSelect={(jobOrder) => applyJobOrder(jobOrder, jobOrderTargetIds)}
      />
    </Dialog>
  )
}

export default React.memo(UploadReceiptDialog)
